// Browser-only persistence for the Grid Mode grid (#2633). Every storage
// access is guarded: when localStorage is blocked or full, the feature turns
// itself off and Grid Mode behaves as if no snapshot existed.

export const SNAPSHOT_KEY_PREFIX = 'terminus:grid-mode:';
export const AUTOSAVE_PREFERENCE_KEY = 'terminus:grid-mode-autosave';
export const SNAPSHOT_VERSION = 1;
export const SNAPSHOT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const parseValid = (raw, now) => {
  let snapshot;
  try {
    snapshot = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!snapshot || snapshot.version !== SNAPSHOT_VERSION) return null;
  if (!Array.isArray(snapshot.rowData)) return null;
  const savedAt = Date.parse(snapshot.savedAt);
  if (Number.isNaN(savedAt) || now - savedAt > SNAPSHOT_MAX_AGE_MS) return null;
  return snapshot;
};

export const readSnapshot = (key, now = Date.now()) => {
  try {
    const storage = window.localStorage;
    const raw = storage.getItem(key);
    if (raw === null) return null;
    const snapshot = parseValid(raw, now);
    if (!snapshot) storage.removeItem(key);
    return snapshot;
  } catch {
    return null;
  }
};

export const removeSnapshot = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Blocked storage: nothing to remove.
  }
};

export const writeSnapshot = (key, { rowData, pinnedRowData }, now = Date.now()) => {
  try {
    window.localStorage.setItem(key, JSON.stringify({
      version: SNAPSHOT_VERSION,
      savedAt: new Date(now).toISOString(),
      rowData,
      pinnedRowData,
    }));
    return true;
  } catch {
    // Blocked or full storage: the snapshot is best-effort. Drop the older
    // entry so a later visit isn't offered data older than what the user last saw.
    removeSnapshot(key);
    return false;
  }
};

export const sweepExpiredSnapshots = (now = Date.now()) => {
  try {
    const storage = window.localStorage;
    const keys = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith(SNAPSHOT_KEY_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => {
      if (!parseValid(storage.getItem(key), now)) storage.removeItem(key);
    });
  } catch {
    // Blocked storage: nothing to sweep.
  }
};

export const isStorageAvailable = () => {
  try {
    return Boolean(window.localStorage);
  } catch {
    return false;
  }
};

// Per browser, not per convention: it's how this person likes to work.
export const readAutosavePreference = () => {
  try {
    return window.localStorage.getItem(AUTOSAVE_PREFERENCE_KEY) !== 'off';
  } catch {
    return true;
  }
};

export const writeAutosavePreference = (enabled) => {
  try {
    window.localStorage.setItem(AUTOSAVE_PREFERENCE_KEY, enabled ? 'on' : 'off');
  } catch {
    // Blocked or full storage: the choice lasts for this page only.
  }
};
